import axios from "axios";
import { MDBTabsPane } from "mdb-react-ui-kit";
import React, { useEffect, useState, useCallback, useRef } from "react";

const BillText = ({ active, bill, ordinal, legislatureOrdinal }) => {
    const [isAdobeDCReady, setIsAdobeDCReady] = useState(false);
    const [noPdfFound, setNoPdfFound] = useState(false);
    const [adobeDCView, setAdobeDCView] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewerError, setViewerError] = useState(null);
    const adobeDivRef = useRef(null);

    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const initializeAdobeDC = useCallback(() => {
        console.log("Initializing Adobe DC, isMobile:", isMobileDevice());

        if (window.AdobeDC) {
            console.log("Adobe DC already available");
            setIsAdobeDCReady(true);
            return;
        }

        if (!document.querySelector('script[src="https://acrobatservices.adobe.com/view-sdk/viewer.js"]')) {
            const script = document.createElement("script");
            script.src = "https://acrobatservices.adobe.com/view-sdk/viewer.js";
            script.async = true;
            script.onload = () => {
                console.log("Adobe DC script loaded");
                document.addEventListener("adobe_dc_view_sdk.ready", () => {
                    console.log("Adobe DC SDK ready event fired");
                    setIsAdobeDCReady(true);
                });
            };
            script.onerror = (error) => {
                console.error("Error loading Adobe DC script:", error);
                setViewerError("Failed to load PDF viewer");
            };
            document.head.appendChild(script);
        } else {
            document.addEventListener("adobe_dc_view_sdk.ready", () => {
                console.log("Adobe DC SDK ready event fired");
                setIsAdobeDCReady(true);
            });
        }
    }, []);

    useEffect(() => {
        if (active) {
            console.log("Tab is active, initializing Adobe DC");
            initializeAdobeDC();
        }
        return () => {
            if (!active && adobeDCView) {
                console.log("Tab is inactive, cleaning up Adobe DC");
                setAdobeDCView(null);
            }
        };
    }, [active, initializeAdobeDC, adobeDCView]);

    useEffect(() => {
        if (isAdobeDCReady && active) {
            console.log("Creating Adobe DC View instance");
            try {
                if(adobeDivRef.current){
                    const adobeDCViewInstance = new window.AdobeDC.View({
                        clientId: process.env.REACT_APP_ADOBE_EMBEDED_API_KEY,
                        divId: "adobe-dc-view",
                    });
                    setAdobeDCView(adobeDCViewInstance);
                    console.log("About to fetch PDF data");
                    fetchData(adobeDCViewInstance);
                }
            } catch (error) {
                console.error("Error creating Adobe DC View instance:", error);
                //setViewerError("Failed to initialize PDF viewer");
            }
        }
    }, [isAdobeDCReady, active]);

    const fetchData = useCallback(
        async (viewerInstance) => {
            setIsLoading(true);
            setNoPdfFound(false);
            const params = new URLSearchParams({
                legislatureOrdinal: `${legislatureOrdinal}`,
                sessionOrdinal: `${ordinal}`,
                billType: `${bill.billType.code}`,
                billNumber: `${bill.billNumber}`,
            });

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_LAWS_BACKEND_HOST}/docs/v1/documents/getBillText?${params.toString()}`,
                    { responseType: 'arraybuffer' }
                );

                if (response.data) {
                    viewerInstance.previewFile(
                        {
                            content: {
                                promise: Promise.resolve(new Uint8Array(response.data)),
                            },
                            metaData: {
                                fileName: `${bill.billType.code}${bill.billNumber}.pdf`,
                            },
                        },
                        { showAnnotationTools: false }
                    );
                } else {
                    setNoPdfFound(true);
                }
            } catch (error) {
                setNoPdfFound(true);
                //setViewerError("Failed to load PDF document");
            } finally {
                setIsLoading(false);
            }
        },
        [ordinal, bill, legislatureOrdinal]
    );

    return (
        <MDBTabsPane show={active}>
            <div className="accordion-body">
                {isLoading && <p className="mt-3">Loading PDF viewer & document...</p>}
                {!isLoading && noPdfFound && (
                    <p className="mt-3">
                        <strong>No document available.</strong>
                    </p>
                )}
                {viewerError && (
                    <p className="mt-3 text-danger">
                        <strong>{viewerError}</strong>
                    </p>
                )}
            </div>
            <div
                ref={adobeDivRef}
                id="adobe-dc-view"
                style={{
                    width: "100%",
                    height: isMobileDevice() ? "100vH" : "80vH",
                    minHeight: "400px",
                    visibility: isLoading ? "hidden" : "visible",
                    display: "block"
                }}
            />
        </MDBTabsPane>
    );
};

export default BillText;